import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X, Search, SlidersHorizontal, CheckCircle2 } from 'lucide-react';
import {
  Dropdown,
  DropdownMenu,
  DropdownCheckboxItem,
  useDropdown,
} from '../inputs/Dropdown';
import type { FilterCategory, SelectedFilter } from '../../types';

interface BrowseFiltersProps {
  categories: FilterCategory[];
  selectedFilters: SelectedFilter[];
  onFilterToggle: (categoryId: string, optionId: string, optionLabel: string, categoryLabel: string) => void;
  onFilterRemove: (categoryId: string, optionId: string) => void;
  onClearAll: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
}

interface FilterTriggerProps {
  label: string;
  selectedCount: number;
  dataBuilder?: string;
}

function FilterTrigger({ label, selectedCount, dataBuilder }: FilterTriggerProps) {
  const { isOpen, setIsOpen } = useDropdown();

  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={() => setIsOpen(!isOpen)}
      data-builder={dataBuilder}
      className={`
        flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
        transition-colors border
        ${selectedCount > 0
          ? 'bg-primary/10 border-primary text-primary'
          : 'bg-transparent border-border text-text-secondary hover:border-primary hover:text-primary'
        }
      `}
    >
      {label}
      {selectedCount > 0 && (
        <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
          {selectedCount}
        </span>
      )}
      <ChevronDown
        className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </motion.button>
  );
}

function FilterDropdown({
  category,
  selectedOptions,
  onToggle,
  alignRight = false,
}: {
  category: FilterCategory;
  selectedOptions: string[];
  onToggle: (optionId: string, optionLabel: string) => void;
  alignRight?: boolean;
}) {
  return (
    <Dropdown>
      <FilterTrigger label={category.label} selectedCount={selectedOptions.length} dataBuilder={`guest-filter-${category.label.toLowerCase()}`} />
      <DropdownMenu align={alignRight ? 'right' : 'left'} className="min-w-[160px]">
        {category.options.map((option) => (
          <DropdownCheckboxItem
            key={option.id}
            checked={selectedOptions.includes(option.id)}
            onChange={() => onToggle(option.id, option.label)}
          >
            {option.dotColor && (
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${option.dotColor}`} />
            )}
            {option.label}
          </DropdownCheckboxItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

export function BrowseFilters({
  categories,
  selectedFilters,
  onFilterToggle,
  onFilterRemove,
  onClearAll,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  actions,
}: BrowseFiltersProps) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const activeCount = selectedFilters.length;

  return (
    <>
      {/* Mobile filter bottom sheet */}
      <AnimatePresence>
        {mobileFiltersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileFiltersOpen(false)}
              className="sm:hidden fixed inset-0 bottom-14 bg-black/50 z-[55]"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="sm:hidden fixed left-0 right-0 bottom-14 z-[56] bg-background-card rounded-t-2xl flex flex-col max-h-[70vh]"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                <span className="font-semibold text-text-primary">Filters</span>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="p-1.5 rounded-full hover:bg-background-elevated transition-colors"
                >
                  <X size={18} className="text-text-secondary" />
                </button>
              </div>

              {/* Filter sections */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
                {categories.map((category, i) => (
                  <div key={category.id}>
                    {i > 0 && <div className="h-px bg-border my-3" />}
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2 px-1">
                      {category.label}
                    </p>
                    {category.options.map((option) => {
                      const isSelected = selectedFilters.some(
                        (f) => f.categoryId === category.id && f.optionId === option.id
                      );
                      return (
                        <button
                          key={option.id}
                          onClick={() => onFilterToggle(category.id, option.id, option.label, category.label)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                            isSelected
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-text-primary hover:bg-background-elevated'
                          }`}
                        >
                          {option.label}
                          {isSelected && <CheckCircle2 size={16} className="text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-4 py-4 border-t border-border">
                <button
                  onClick={() => { onClearAll(); }}
                  className="flex-1 py-2.5 rounded-full border border-border text-sm font-semibold text-text-secondary hover:bg-background-elevated transition-colors"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="flex-1 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div>
        {/* Mobile: search + filters button + actions on one line */}
        <div className="flex sm:hidden items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-9 pr-3 py-2 bg-transparent border border-border rounded-lg text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-elevated rounded transition-colors"
              >
                <X className="w-3 h-3 text-text-secondary" />
              </button>
            )}
          </div>
          {categories.length > 0 && (
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className={`relative flex items-center justify-center px-2 py-2.5 rounded-lg border transition-colors shrink-0 ${
                activeCount > 0
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-transparent border-border text-text-secondary'
              }`}
            >
              <SlidersHorizontal size={16} />
              {activeCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-semibold">
                  {activeCount}
                </span>
              )}
            </button>
          )}
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>

        {/* Desktop: search + individual filter dropdowns */}
        <div className="hidden sm:flex flex-wrap items-center gap-3 mt-0">
          <div className="relative flex-1 min-w-0 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-9 pr-3 py-2 bg-transparent border border-border rounded-lg text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-elevated rounded transition-colors"
              >
                <X className="w-3 h-3 text-text-secondary" />
              </button>
            )}
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((category, index) => (
                <FilterDropdown
                  key={category.id}
                  category={category}
                  selectedOptions={selectedFilters
                    .filter((f) => f.categoryId === category.id)
                    .map((f) => f.optionId)}
                  onToggle={(optionId, optionLabel) =>
                    onFilterToggle(category.id, optionId, optionLabel, category.label)
                  }
                  alignRight={!actions && index === categories.length - 1}
                />
              ))}
            </div>
          )}

          {actions && (
            <div className="flex items-center gap-2 ml-auto">
              {actions}
            </div>
          )}
        </div>

        {/* Selected filter pills (both breakpoints) */}
        <AnimatePresence>
          {selectedFilters.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap items-center gap-2 mt-3"
            >
              {selectedFilters.map((filter) => (
                <motion.span
                  key={`${filter.categoryId}-${filter.optionId}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-sm rounded-full"
                >
                  <span className="text-primary/70 text-xs">{filter.categoryLabel}:</span>
                  {filter.optionLabel}
                  <button
                    onClick={() => onFilterRemove(filter.categoryId, filter.optionId)}
                    className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              ))}
              <button
                onClick={onClearAll}
                className="text-sm text-error hover:text-error/80 transition-colors"
              >
                Clear all
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
